import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useIsMobile } from "./askai/useMediaQuery";

const LOGO_URL =
  "https://procurexuatstg.blob.core.windows.net/procurexuatblob/orgs/org%20logo/orgLogo__f51cae49-f301-4d0b-8d72-3572b1bf0fe3.png";

// ─────────────────────────────────────────────────────────────────────────────
// ProcureX blue palette + typography
// ─────────────────────────────────────────────────────────────────────────────

const BLUE = {
  sky: "#60a5fa",
  brand: "#2563eb",
  deep: "#1e3a8a",
  soft: "#dbeafe",
  ink: "#0f172a",
  muted: "#64748b",
};

const SERIF =
  '"Instrument Serif", "Iowan Old Style", "Apple Garamond", Georgia, serif';
const SANS =
  '"Inter", ui-sans-serif, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Procurement-flavored rotating step labels. Each cycles every ~4.5 s.
const STEPS: Array<{ lead: string; accent: string; sub: string }> = [
  {
    lead: "Reviewing supplier",
    accent: "bids",
    sub: "Pulling structured bid data and quotes",
  },
  {
    lead: "Cross-checking",
    accent: "compliance",
    sub: "Verifying technical fit and certifications",
  },
  {
    lead: "Comparing",
    accent: "savings",
    sub: "BAFO rounds against initial quotes",
  },
  {
    lead: "Detecting",
    accent: "risks",
    sub: "Scanning bidder history for red flags",
  },
  {
    lead: "Crafting",
    accent: "insights",
    sub: "Synthesising the recommendation",
  },
  {
    lead: "Almost",
    accent: "there",
    sub: "Finalising scores and highlights",
  },
];

const STAGE = 260; // px — radar stage size
const STEP_MS = 4500;

interface Props {
  tenderTitle?: string;
  // Optional — set to true to also dim/cover background. Defaults to inline.
  visible?: boolean;
}

const RegenerateLoader: React.FC<Props> = ({
  tenderTitle,
  visible = true,
}) => {
  const [stepIdx, setStepIdx] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!visible) {
      setStepIdx(0);
      return;
    }
    const id = window.setInterval(() => {
      setStepIdx((i) => (i + 1) % STEPS.length);
    }, STEP_MS);
    return () => window.clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "24px 12px 36px" : "48px 24px 60px",
        minHeight: isMobile ? 440 : 580,
        fontFamily: SANS,
        overflow: "hidden",
      }}
    >
      {/* Soft ambient blue blobs behind everything */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "5%",
          left: "10%",
          width: 320,
          height: 320,
          borderRadius: "50%",
          background:
            "radial-gradient(closest-side, rgba(96, 165, 250, 0.18), rgba(96, 165, 250, 0) 70%)",
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          bottom: "5%",
          right: "10%",
          width: 360,
          height: 360,
          borderRadius: "50%",
          background:
            "radial-gradient(closest-side, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0) 70%)",
          filter: "blur(12px)",
          pointerEvents: "none",
        }}
      />

      {/* Top brand pill */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "5px 14px 5px 10px",
          borderRadius: 999,
          background: "#fff",
          border: `1px solid ${BLUE.soft}`,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 1.4,
          color: BLUE.brand,
          textTransform: "uppercase",
          boxShadow: "0 4px 14px -10px rgba(34, 99, 179, 0.3)",
          zIndex: 1,
        }}
      >
        <motion.span
          animate={{ opacity: [1, 0.35, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: BLUE.brand,
          }}
        />
        ProcureX IntelliSuite · AI Regenerating
      </motion.div>

      {tenderTitle && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.4 }}
          style={{
            fontSize: 13,
            color: BLUE.muted,
            marginTop: 12,
            zIndex: 1,
          }}
        >
          Refreshing AI insights for Tender{" "}
          <strong style={{ color: BLUE.ink, fontWeight: 600 }}>
            {tenderTitle}
          </strong>
          …
        </motion.div>
      )}

      {/* ────────── Radar scope ────────── */}
      <div
        style={{
          width: isMobile ? STAGE * 0.7 : STAGE,
          height: isMobile ? STAGE * 0.7 : STAGE,
          marginTop: isMobile ? 16 : 28,
          marginBottom: isMobile ? 16 : 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
      <div
        style={{
          position: "relative",
          width: STAGE,
          height: STAGE,
          // Scale the entire radar down on mobile rather than re-sizing
          // each internal element — preserves the proportions perfectly.
          transform: isMobile ? "scale(0.7)" : undefined,
          transformOrigin: "center",
        }}
      >
        {/* Soft scope tint — gives the radar a "screen" feel */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BLUE.brand}08 0%, ${BLUE.brand}04 60%, transparent 100%)`,
            pointerEvents: "none",
          }}
        />

        {/* Concentric range rings (static) — at 33%, 66%, 100% radius */}
        <svg
          width={STAGE}
          height={STAGE}
          viewBox="0 0 260 260"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
          }}
        >
          <circle
            cx="130"
            cy="130"
            r="125"
            fill="none"
            stroke={BLUE.brand}
            strokeOpacity="0.16"
            strokeWidth="1"
          />
          <circle
            cx="130"
            cy="130"
            r="85"
            fill="none"
            stroke={BLUE.brand}
            strokeOpacity="0.14"
            strokeWidth="1"
          />
          <circle
            cx="130"
            cy="130"
            r="45"
            fill="none"
            stroke={BLUE.brand}
            strokeOpacity="0.12"
            strokeWidth="1"
          />
          {/* Crosshair lines */}
          <line
            x1="5"
            y1="130"
            x2="255"
            y2="130"
            stroke={BLUE.brand}
            strokeOpacity="0.1"
            strokeWidth="1"
          />
          <line
            x1="130"
            y1="5"
            x2="130"
            y2="255"
            stroke={BLUE.brand}
            strokeOpacity="0.1"
            strokeWidth="1"
          />
        </svg>

        {/* Dashed outer ring — slow rotation gives the scope life */}
        <motion.svg
          animate={{ rotate: 360 }}
          transition={{ duration: 24, ease: "linear", repeat: Infinity }}
          width={STAGE}
          height={STAGE}
          viewBox="0 0 260 260"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
          }}
        >
          <circle
            cx="130"
            cy="130"
            r="128"
            fill="none"
            stroke={BLUE.brand}
            strokeOpacity="0.32"
            strokeWidth="1"
            strokeDasharray="4 6"
          />
        </motion.svg>

        {/* THE RADAR SWEEP — conic-gradient wedge rotating around center.
            The wedge fades from solid (leading edge) through a 90° trail,
            so it reads like a classic sonar sweep. */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3.2, ease: "linear", repeat: Infinity }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 250,
            height: 250,
            marginTop: -125,
            marginLeft: -125,
            borderRadius: "50%",
            background: `conic-gradient(from -90deg,
              transparent 0deg,
              transparent 270deg,
              ${BLUE.brand}33 320deg,
              ${BLUE.brand}88 358deg,
              ${BLUE.brand}00 360deg)`,
            // Fade the inner edge so the beam looks like it emanates from
            // the orb, not from a hard center.
            maskImage:
              "radial-gradient(circle, transparent 12%, black 22%, black 100%)",
            WebkitMaskImage:
              "radial-gradient(circle, transparent 12%, black 22%, black 100%)",
            pointerEvents: "none",
          }}
        />

        {/* Blips — 5 dots at static positions that pulse like targets the
            radar has picked up. Different delays so they don't sync. */}
        {[
          { x: 0.32, y: 0.38, delay: 0.0 },
          { x: 0.7, y: 0.42, delay: 0.6 },
          { x: 0.62, y: 0.68, delay: 1.2 },
          { x: 0.36, y: 0.66, delay: 1.8 },
          { x: 0.52, y: 0.28, delay: 2.4 },
        ].map((b, i) => (
          <motion.span
            key={`blip-${i}`}
            animate={{
              scale: [0.6, 1.4, 0.6],
              opacity: [0.2, 1, 0.2],
            }}
            transition={{
              duration: 2.6,
              ease: "easeInOut",
              repeat: Infinity,
              delay: b.delay,
            }}
            style={{
              position: "absolute",
              left: `${b.x * 100}%`,
              top: `${b.y * 100}%`,
              width: 7,
              height: 7,
              marginLeft: -3.5,
              marginTop: -3.5,
              borderRadius: "50%",
              background: BLUE.brand,
              boxShadow: `0 0 10px ${BLUE.brand}bb`,
              pointerEvents: "none",
            }}
          />
        ))}

        {/* Single outward ripple — subtle pulse from center */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0.45 }}
          animate={{ scale: 2.6, opacity: 0 }}
          transition={{
            duration: 3,
            ease: "easeOut",
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 80,
            height: 80,
            marginTop: -40,
            marginLeft: -40,
            borderRadius: "50%",
            border: `1px solid ${BLUE.brand}`,
            opacity: 0.4,
            pointerEvents: "none",
          }}
        />

        {/* Center glow */}
        <motion.div
          animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.3, 0.55] }}
          transition={{
            duration: 2.8,
            ease: "easeInOut",
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 90,
            height: 90,
            marginTop: -45,
            marginLeft: -45,
            borderRadius: "50%",
            background: `radial-gradient(closest-side, ${BLUE.brand}40 0%, ${BLUE.brand}00 70%)`,
            pointerEvents: "none",
          }}
        />

        {/* Org logo at the radar's center — soft white backdrop, no border. */}
        <motion.div
          animate={{ scale: [1, 1.06, 1] }}
          transition={{
            duration: 3,
            ease: "easeInOut",
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 56,
            height: 56,
            marginTop: -28,
            marginLeft: -28,
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            boxShadow: `0 10px 24px -8px ${BLUE.brand}55`,
            zIndex: 2,
          }}
        >
          <img
            src={LOGO_URL}
            alt="ProcureX"
            style={{
              width: "76%",
              height: "76%",
              objectFit: "contain",
              display: "block",
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </motion.div>
      </div>
      </div>

      {/* Step label with serif italic accent */}
      <div
        style={{
          minHeight: 80,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: 560,
          textAlign: "center",
          zIndex: 1,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={stepIdx}
            initial={{ opacity: 0, y: 8, filter: "blur(2px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -8, filter: "blur(2px)" }}
            transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              style={{
                fontSize: isMobile ? 20 : 26,
                fontWeight: 600,
                color: BLUE.ink,
                letterSpacing: -0.4,
                lineHeight: 1.25,
              }}
            >
              {STEPS[stepIdx].lead}{" "}
              <span
                style={{
                  fontFamily: SERIF,
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: isMobile ? 24 : 30,
                  background: `linear-gradient(120deg, ${BLUE.sky} 0%, ${BLUE.brand} 50%, ${BLUE.deep} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {STEPS[stepIdx].accent}
              </span>
              …
            </div>
            <div
              style={{
                fontSize: isMobile ? 12.5 : 13.5,
                color: BLUE.muted,
                marginTop: 10,
                lineHeight: 1.55,
                padding: isMobile ? "0 8px" : 0,
              }}
            >
              {STEPS[stepIdx].sub}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Three dots progress */}
      <div
        style={{
          display: "inline-flex",
          gap: 6,
          marginTop: 18,
          zIndex: 1,
        }}
      >
        {[0, 0.15, 0.3].map((d, i) => (
          <motion.span
            key={i}
            animate={{ y: [0, -4, 0], opacity: [0.35, 1, 0.35] }}
            transition={{
              duration: 1.2,
              ease: "easeInOut",
              repeat: Infinity,
              delay: d,
            }}
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: BLUE.brand,
              display: "inline-block",
            }}
          />
        ))}
      </div>

      {/* Time hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        style={{
          marginTop: 22,
          fontSize: 12,
          color: BLUE.muted,
          zIndex: 1,
        }}
      >
        This usually takes about{" "}
        <span style={{ color: BLUE.ink, fontWeight: 500 }}>30 seconds</span>
      </motion.div>
    </div>
  );
};

export default RegenerateLoader;
